// src/components/TransactionResultPopup.jsx - Update to fix token display
import React, { useEffect, useState } from 'react';

const TransactionResultPopup = ({ 
  isOpen, 
  onClose, 
  onRefresh, // For manual refresh
  status = 'pending', // 'pending', 'success', 'failed', 'cancelled'
  actionType, 
  amount,
  message,
  txHash,
  provider, // ethers provider for checking transaction status
  positions = [], // Add positions data for enhanced display
  tokenPrices = {}, // Add token price data 
  fundData = {}, // Add fund data for better display
  autoCloseTime = 5000 // Default to 5 seconds auto-close for success/failure
}) => {
  // Local state
  const [timeRemaining, setTimeRemaining] = useState(autoCloseTime / 1000);
  const [blockConfirmations, setBlockConfirmations] = useState(0);
  const [timeSincePending, setTimeSincePending] = useState(0);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  
  // Auto close the popup after specified time (only for success/failed/cancelled)
  useEffect(() => {
    let timer;
    if (isOpen && status !== 'pending' && autoCloseTime > 0) {
      // Start countdown
      const countdownInterval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      timer = setTimeout(() => {
        onClose();
      }, autoCloseTime);
      
      return () => {
        clearTimeout(timer);
        clearInterval(countdownInterval);
      };
    }
    return () => {};
  }, [isOpen, onClose, autoCloseTime, status]);
  
  // Track time spent in pending state
  useEffect(() => {
    let interval;
    if (isOpen && status === 'pending') {
      // Track time in pending state to show refresh button after a while
      interval = setInterval(() => {
        setTimeSincePending(prev => {
          const newValue = prev + 1;
          
          // After 10 seconds in pending state, check status if we have txHash
          if (newValue === 10 && txHash && provider && !isCheckingStatus) {
            checkTransactionStatus();
          }
          
          // After 20 seconds in pending state, show the refresh button
          if (newValue >= 20 && !showRefreshButton) {
            setShowRefreshButton(true);
          }
          
          return newValue;
        });
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isOpen, status, txHash, provider, timeSincePending, showRefreshButton, isCheckingStatus]);
  
  // Function to check transaction status
  const checkTransactionStatus = async () => {
    if (!txHash || !provider || isCheckingStatus) return;
    
    setIsCheckingStatus(true);
    try {
      // First try to get transaction receipt
      const receipt = await provider.getTransactionReceipt(txHash);
      
      if (receipt) {
        // Transaction is mined
        if (receipt.status === 1) {
          // Success
          if (onRefresh) onRefresh();
        }
      } else {
        // No receipt yet, get transaction and check confirmations
        const tx = await provider.getTransaction(txHash);
        if (tx && tx.blockNumber) {
          const currentBlock = await provider.getBlockNumber();
          const confirmations = currentBlock - tx.blockNumber + 1;
          setBlockConfirmations(confirmations);
          
          // If we have enough confirmations but no receipt status, assume success
          if (confirmations >= 3) {
            if (onRefresh) onRefresh();
          }
        }
      }
    } catch (error) {
      console.error("Error checking transaction status:", error);
    } finally {
      setIsCheckingStatus(false);
    }
  };
  
  // Check confirmations for pending transactions
  useEffect(() => {
    let interval;
    if (isOpen && status === 'pending' && txHash && provider) {
      interval = setInterval(() => {
        checkTransactionStatus();
      }, 5000); // Check every 5 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isOpen, status, txHash, provider]);

  // Reset state when popup opens
  useEffect(() => {
    if (isOpen) {
      setTimeRemaining(autoCloseTime / 1000);
      setBlockConfirmations(0);
      setTimeSincePending(0);
      setShowRefreshButton(false);
      setIsCheckingStatus(false);
    }
  }, [isOpen, autoCloseTime]);

  if (!isOpen) return null;

  // Format action type string for display
  const formatActionType = (type) => {
    if (!type) return '';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Determine the correct token unit to display based on action type
  const getTokenUnit = () => {
    // Default to ETH for most operations
    let unit = 'ETH';
    
    // Check if the action type indicates MCIF operations
    if (actionType) {
      const actionLower = actionType.toLowerCase();
      
      if (actionLower.includes('mcif') || 
          actionLower.includes('fund token') || 
          actionLower.includes('token') || 
          actionLower === 'mint' || 
          actionLower === 'redeem') {
        unit = 'MCIF';
      }
      
      // Check for specific token redemption
      if (actionLower.includes('redeem token (')) {
        // Extract token symbol from action type like "redeem token (USDC)"
        const match = actionLower.match(/redeem token \(([^)]+)\)/i);
        if (match && match[1]) {
          unit = match[1].toUpperCase();
        }
      }
    }
    
    return unit;
  };

  // Determine status colors and title
  const getStatusConfig = () => {
    switch (status) {
      case 'success':
        return { 
          bg: 'bg-green-100', 
          text: 'text-green-700', 
          border: 'border-green-500', 
          icon: 'text-green-600',
          title: 'Transaction Successful'
        };
      case 'failed':
        return { 
          bg: 'bg-red-100', 
          text: 'text-red-700', 
          border: 'border-red-500', 
          icon: 'text-red-600',
          title: 'Transaction Failed'
        };
      case 'cancelled':
        return {
          bg: 'bg-amber-100',
          text: 'text-amber-700',
          border: 'border-amber-500',
          icon: 'text-amber-600',
          title: 'Transaction Cancelled'
        };
      case 'pending':
      default:
        return { 
          bg: 'bg-blue-100', 
          text: 'text-blue-700', 
          border: 'border-blue-500', 
          icon: 'text-blue-600',
          title: 'Transaction Pending'
        };
    }
  };
  
  const statusConfig = getStatusConfig();

  // Generate appropriate icon based on status
  const StatusIcon = () => {
    if (status === 'success') {
      return (
        <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${statusConfig.bg} sm:mx-0 sm:h-10 sm:w-10 mb-4`}>
          <svg className={`h-6 w-6 ${statusConfig.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    } else if (status === 'failed') {
      return (
        <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${statusConfig.bg} sm:mx-0 sm:h-10 sm:w-10 mb-4`}>
          <svg className={`h-6 w-6 ${statusConfig.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    } else if (status === 'cancelled') {
      return (
        <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${statusConfig.bg} sm:mx-0 sm:h-10 sm:w-10 mb-4`}>
          <svg className={`h-6 w-6 ${statusConfig.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    } else {
      // Pending spinner animation
      return (
        <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${statusConfig.bg} sm:mx-0 sm:h-10 sm:w-10 mb-4`}>
          <svg className={`animate-spin h-6 w-6 ${statusConfig.icon}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      );
    }
  };

  // Handle refresh action
  const handleRefresh = () => {
    // Manually check transaction status immediately
    checkTransactionStatus();
    // Also call the parent's refresh function
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative border-l-4 ${statusConfig.border}`}>
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="flex flex-col items-center">
          <StatusIcon />
          
          <h2 className={`text-xl font-bold ${statusConfig.text} mb-2`}>
            {statusConfig.title}
          </h2>
          
          <p className="text-gray-600 text-center mb-4">
            {message || (
              status === 'pending' 
                ? `Your ${formatActionType(actionType)} transaction is being processed on the blockchain.` 
                : status === 'cancelled'
                  ? `Your ${formatActionType(actionType)} transaction was cancelled.`
                  : `Your ${formatActionType(actionType)} ${status === 'success' ? 'was successful' : 'failed to complete'}.`
            )}
          </p>
          
          {/* Transaction Details */}
          <div className="bg-gray-50 p-4 rounded-lg w-full mb-4">
            {actionType && (
              <div className="flex justify-between mb-2">
                <span className="font-medium">Action:</span>
                <span className="font-medium">{formatActionType(actionType)}</span>
              </div>
            )}
            
            {amount && (
              <div className="flex justify-between mb-2">
                <span className="font-medium">Amount:</span>
                <span className="font-mono">{amount} {getTokenUnit()}</span>
              </div>
            )}
            
            {txHash && (
              <div className="flex flex-col mb-2">
                <span className="font-medium mb-1">Transaction Hash:</span>
                <a 
                  href={`https://etherscan.io/tx/${txHash}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-700 truncate font-mono text-sm break-all"
                >
                  {txHash}
                </a>
              </div>
            )}
            
            {status === 'pending' && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Status:</span>
                  <span className="font-medium text-blue-600">
                    {isCheckingStatus ? 'Checking status...' : (
                      blockConfirmations > 0 
                        ? `${blockConfirmations} confirmation${blockConfirmations !== 1 ? 's' : ''}` 
                        : 'Waiting for confirmation...'
                    )}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${Math.min(blockConfirmations * 33, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Your transaction is being processed. Please wait for blockchain confirmation.
                </p>
                
                {/* Show refresh button after 20 seconds in pending state */}
                {showRefreshButton && (
                  <div className="mt-3 pt-2 border-t border-gray-200">
                    <p className="text-sm text-amber-600 mb-2">
                      Taking longer than expected? Your transaction may have already completed.
                    </p>
                    <button
                      onClick={handleRefresh}
                      disabled={isCheckingStatus}
                      className={`w-full py-2 px-4 bg-amber-500 text-white rounded font-medium hover:bg-amber-600 transition ${isCheckingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isCheckingStatus ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Checking...
                        </span>
                      ) : (
                        'Refresh & Check Status'
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {status === 'cancelled' && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <p className="text-sm text-amber-600">
                  You cancelled this transaction in your wallet. No blockchain transaction was submitted, and no fees were charged.
                </p>
              </div>
            )}
            
            {(status === 'success' || status === 'failed' || status === 'cancelled') && autoCloseTime > 0 && (
              <div className="text-xs text-gray-500 text-center mt-2">
                This message will close in {timeRemaining} seconds
              </div>
            )}
          </div>
          
          <div className="w-full flex justify-center">
            {status === 'pending' ? (
              <button
                onClick={onClose}
                className={`px-6 py-2 rounded-lg font-medium text-white bg-blue-500 hover:bg-blue-600 focus:ring-blue-500
                  focus:outline-none focus:ring-2 focus:ring-offset-2`}
              >
                Continue in Background
              </button>
            ) : status === 'cancelled' ? (
              <button
                onClick={onClose}
                className={`px-6 py-2 rounded-lg font-medium text-white bg-amber-500 hover:bg-amber-600 focus:ring-amber-500
                  focus:outline-none focus:ring-2 focus:ring-offset-2`}
              >
                Close
              </button>
            ) : (
              <button
                onClick={onClose}
                className={`px-6 py-2 rounded-lg font-medium text-white 
                  ${status === 'success' 
                    ? 'bg-green-500 hover:bg-green-600 focus:ring-green-500' 
                    : 'bg-red-500 hover:bg-red-600 focus:ring-red-500'
                  }
                  focus:outline-none focus:ring-2 focus:ring-offset-2`}
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionResultPopup;